import { useRecoilValue } from 'recoil';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Logo from './../../assets/STATO-INTOO-LOGO-MY-INTOO-VERS-1.png';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FilledInput from '@mui/material/FilledInput';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { styled } from 'styled-components';
import React from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { history } from '../../helpers/history';
import { authAtom } from '../../states/usuarios/AuthState';
import { useUsuarioActions } from '../../states/usuarios/usuario.actions';
import { FormControlStato, GridContainer } from '../../commons/styleds/styleds';
import { GridItem } from '../../components/Layout';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../commons/useTranslasion';
import LanguageSelector from '../../components/LanguageSelector';
import { createGlobalStyle } from 'styled-components';
import { themeConstants } from '../../commons/theme';
import { IAuth } from '../../commons/types/usuatios';
import DialogSelecaoUnidadeOperacional from '../../components/DialogSelecaoUnidadeOperacional';

const Login: React.FC = () => {
  const { translate } = useTranslation();
  const auth = useRecoilValue(authAtom);
  const usuarioActions = useUsuarioActions();
  const [email, setEmail] = useState<string>('');
  const [senha, setSenha] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { colors } = themeConstants;
  const [mostrarSenha, setMostrarSenha] = React.useState(false);
  const [mostrarSelecao, setMostrarSelecao] = useState<boolean>(false);
  const [usuarios, setUsuarios] = useState<IAuth[]>([]);

  const handleClickMostrarSenha = () => setMostrarSenha((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (email && senha && !loading) {
        onSubmit(event);
      }
    }
  };

  const handleUsuarioSelect = (usuario: IAuth) => {
    setMostrarSelecao(false);
    // Completar o login com o usuário selecionado
    usuarioActions.completeLogin(usuario);
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    if (!loading) {
      setLoading(true);
      try {
        const result = await usuarioActions.login(email, senha);
        
        if (result.multipleUnits) {
          // Múltiplas unidades operacionais - mostrar dialog de seleção
          setUsuarios(result.usuarios || []);
          setMostrarSelecao(true);
          setLoading(false);
        } else {
          // Usuário único - login já foi completado na action
          setLoading(false);
        }
      } catch (erro) {
        setLoading(false);
        // Manter tratamento de erro existente
      }
    }
  };

  useEffect(() => {
    if (auth!!) history.push('/');
  }, [auth]);

  return (
    <>
      <LoginGlobalStyle />
      <GridContainer
        container
        sx={{
          height: { xs: 'auto', md: '100vh' },
          minHeight: { xs: 'fill-available', md: '100vh' },
          overflow: { xs: 'auto', md: 'hidden' },
          maxHeight: { xs: 'none', md: '100vh' },
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: {xs: 'column', md: 'row'}
        }}>
        {/* Logo - Visível apenas no desktop (md+) */}
        <GridItem 
          item 
          xs={12} 
          md={6} 
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <img src={Logo} alt='logo' width={'80%'} />
        </GridItem>
        
        <GridItem 
          item 
          xs={12} 
          md={6} 
          color={'var(--blue2)'}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-start', md: 'center' },
            minHeight: { xs: 'auto', md: '100vh' },
            pt: { xs: 4, md: 0 },
            pb: { xs: 6, md: 0 }
          }}
        >
          {/* Logo - Visível apenas no mobile (xs-sm) */}
          <Box 
            sx={{ 
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              mb: 4,
              mt: 2
            }}
          >
            <img src={Logo} alt='logo' width={'60%'} />
          </Box>
          <Grid sx={{
            marginRight: '10px', 
            marginLeft: '10px', 
            display: 'flex', 
            alignItems: 'center',
            position: "absolute",
            top: "15px",
            right: "15px"
          }}>
            <LanguageSelector />
          </Grid>

          <Grid 
            item 
            xs={10} 
            md={9} 
            sx={{ 
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              mt: { xs: 2, md: 0 },
              position: 'relative',
              zIndex: 1
            }}>
            <form onSubmit={onSubmit}>
              <GridItem item xs={12} md={12} color={'var(--blue2)'}>
                <TypographyLogin variant='h3'>
                  {translate('bemVindoPortalConsultor')}
                </TypographyLogin>
                <FormControlStato
                  sx={{ width: '100%', marginBottom: '15px' }}
                  variant='filled'
                >
                  <TextField
                    id='filled-basic'
                    label={translate('email')}
                    type='email'
                    required
                    variant='filled'
                    value={email}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setEmail(event.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                  />
                </FormControlStato>

                <FormControlStato sx={{ width: '100%' }} variant='filled'>
                  <InputLabel htmlFor='senha'>{translate('senha')}</InputLabel>
                  <FilledInput
                    id='senha'
                    type={mostrarSenha ? 'text' : 'password'}
                    value={senha}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setSenha(event.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={handleClickMostrarSenha}
                          onMouseDown={handleMouseDownPassword}
                          edge='end'
                        >
                          {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControlStato>
              </GridItem>
              <GridItem
                item
                xs={12}
                md={12}
                color={'var(--blue2)'}
                style={{ paddingTop: '15px' }}
              >
                <Grid item xs={6} md={6}>
                  <Link to="/redefinir-senha" style={{ textDecoration: 'none' }}>
                    <Typography 
                      sx={{
                        color: { xs: colors.blue.main, md: colors.white }
                      }}
                    >
                      {translate('esqueceuSenha')}
                    </Typography>
                  </Link>
                </Grid>
                <Grid item xs={6} md={6} style={{ textAlign: 'right' }}>
                  <Button 
                    type='submit'
                    disabled={loading}
                    sx={{ 
                      backgroundColor: 'primary.light',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress 
                        size={24} 
                        sx={{ 
                          color: 'white'
                        }} 
                      />
                    ) : (
                      translate('login')
                    )}
                  </Button>
                </Grid>
              </GridItem>
            </form>
          </Grid>
        </GridItem>
      </GridContainer>
      
      <DialogSelecaoUnidadeOperacional
        open={mostrarSelecao}
        usuarios={usuarios}
        onUsuarioSelect={handleUsuarioSelect}
        onCancel={() => setMostrarSelecao(false)}
      />
    </>
  );
};

export const SpanStatoLogin = styled.span`
  margin-top: 10px;
  font-size: 14px;
`;

const TypographyLogin = styled(Typography)`
  color: #f3f3f3 !important;
  margin-bottom: 20px !important;

  @media (max-width: 900px) {
    color: #585858 !important;
    text-align: center;
  }
`;

const LoginGlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    overflow: auto;
    min-height: fill-available;
    min-height: -webkit-fill-available;
    height: auto;
    @media (min-width: 900px) {
      overflow: hidden;
      height: 100vh;
    }
  }
  html {
    height: fill-available;
    height: -webkit-fill-available;
  }
`;

export default Login;
